/**
 * Envío AP-14 · «Adaptar video»: recorte, resolución, fps, peso máximo y audio
 * de un video existente. Crea un MP4 NUEVO en la Galería; el original no cambia.
 * Reusa el recortador de la cámara (AP-11) y el FFmpeg del servidor.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Scaling, X, Loader2, Check, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import VideoTrimmer from './VideoTrimmer';
import { adaptVideo } from '../../services/cameraClipService';
import { clampTrim, fmtSec } from '../../utils/cameraCapture';
import { fmtBytes } from '../../utils/imageEdit';
import {
  FPS, HEIGHTS, MAX_ADAPT_SECONDS, VIDEO_PRESETS, estimateBytes, outputDims, videoPreset,
} from '../../utils/videoAdapt';
import '../../styles/camera.css';

export default function VideoAdaptModal({ source, initialPreset = null, onClose, onDone }) {
  const pre = videoPreset(initialPreset);
  const [preset, setPreset] = useState(pre?.key || 'custom');
  const [height, setHeight] = useState(pre ? pre.height : null);
  const [fps, setFps] = useState(pre ? pre.fps : 30);
  const [maxMB, setMaxMB] = useState(pre ? pre.maxMB : null);
  const [audio, setAudio] = useState(pre ? pre.audio : true);
  const [meta, setMeta] = useState(null);                 // {duration,w,h}
  const [trim, setTrim] = useState({ start: 0, end: 0 });
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const vref = useRef(null);

  const limits = videoPreset(preset) || { minSec: 1, maxSec: MAX_ADAPT_SECONDS };
  const dest = { max: limits.maxSec, min: limits.minSec, label: videoPreset(preset)?.label || 'El video' };

  const onMeta = () => {
    const v = vref.current;
    if (!v) return;
    const duration = Number.isFinite(v.duration) ? v.duration : 0;
    setMeta({ duration, w: v.videoWidth, h: v.videoHeight });
    setTrim(clampTrim(0, duration, duration, dest));
  };

  const pickPreset = (key) => {
    const p = videoPreset(key);
    setPreset(key);
    if (!p) return;
    setHeight(p.height); setFps(p.fps); setMaxMB(p.maxMB); setAudio(p.audio);
    if (meta) setTrim(clampTrim(trim.start, trim.end, meta.duration, { max: p.maxSec }));
  };
  const custom = (fn) => (v) => { setPreset('custom'); fn(v); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) { e.stopPropagation(); onClose?.(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [saving, onClose]);

  const onTime = () => {
    const v = vref.current;
    if (!v) return;
    if (v.currentTime >= trim.end - 0.03 || v.currentTime < trim.start - 0.05) v.currentTime = trim.start;
    setPlayhead(v.currentTime);
  };
  const toggle = () => {
    const v = vref.current;
    if (!v) return;
    if (v.paused) { if (v.currentTime < trim.start || v.currentTime >= trim.end) v.currentTime = trim.start; v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const seek = (t) => { const v = vref.current; if (v) { v.currentTime = Math.min(Math.max(t, trim.start), trim.end); setPlayhead(v.currentTime); } };

  const len = trim.end - trim.start;
  const problem = !meta ? null
    : len < limits.minSec - 1e-6 ? `${dest.label}: el tramo tiene que durar al menos ${fmtSec(limits.minSec)}.`
      : len > limits.maxSec + 1e-6 ? `El tramo puede durar hasta ${fmtSec(limits.maxSec)}.` : null;
  const dims = meta ? outputDims(meta.w, meta.h, height) : null;
  const est = meta ? estimateBytes({ seconds: len, sourceH: meta.h, height, maxMB, audio }) : null;

  const save = async () => {
    if (!meta || problem) return;
    setSaving(true);
    try {
      const out = await adaptVideo({
        url: source.url, start: trim.start, end: trim.end, height, fps,
        maxMB: maxMB || null, audio, name: source.label || null,
      });
      toast.success('Video adaptado guardado en tu Galería', {
        description: `${fmtSec(out.duration)} · ${out.width}×${out.height}. El original no cambió.`, position: 'bottom-right',
      });
      onDone?.(out);
      onClose?.();
    } catch (err) {
      toast.error(err?.userMessage || 'No se pudo adaptar el video. Probá de nuevo.', { position: 'bottom-right', duration: 8000 });
      setSaving(false);
    }
  };

  return createPortal(
    <div className="cam-overlay nodrag nowheel" onPointerDown={(e) => e.stopPropagation()} data-testid="va-modal">
      <section className="cam-modal" role="dialog" aria-modal="true" aria-label="Adaptar video">
        <header className="cam-head">
          <Scaling size={20} className="cam-accent" aria-hidden="true" />
          <div className="cam-head-txt">
            <span className="cam-kicker">ADAPTAR VIDEO</span>
            <span className="cam-target">{source?.label || 'Video'}</span>
          </div>
          <button type="button" className="cam-icon-btn" onClick={onClose} disabled={saving} aria-label="Cerrar"><X size={18} /></button>
        </header>
        <div className="cam-body">
          <div className="cam-main">
            <div className="cam-stage">
              {/* eslint-disable-next-line */}
              <video ref={vref} src={source?.url} className="cam-video" playsInline preload="metadata"
                onLoadedMetadata={onMeta} onTimeUpdate={onTime} onPause={() => setPlaying(false)}
                onError={() => setLoadError(true)} data-testid="va-video" />
              {meta && (
                <button type="button" className="cam-play" onClick={toggle} aria-label={playing ? 'Pausar' : 'Reproducir'}>
                  {playing ? <Pause size={24} aria-hidden="true" /> : <Play size={24} aria-hidden="true" />}
                </button>
              )}
              {!meta && !loadError && <div className="cam-center"><Loader2 size={24} className="cam-spin" aria-hidden="true" /><span>Abriendo el video…</span></div>}
              {loadError && <div className="cam-center"><span>No se pudo abrir el video.</span></div>}
              {meta && <span className="cam-pill tl">{meta.w}×{meta.h} · {fmtSec(meta.duration)}</span>}
            </div>
            {meta && (
              <VideoTrimmer total={meta.duration} start={trim.start} end={trim.end} playhead={playhead}
                dest={dest} onChange={setTrim} onSeek={seek} />
            )}
          </div>
          <aside className="cam-side" aria-label="Ajustes">
            <div className="cam-field">
              <span className="cam-label">Destino</span>
              <div className="cam-presets" role="group" aria-label="Destino">
                {VIDEO_PRESETS.map((p) => (
                  <button key={p.key} type="button" aria-pressed={preset === p.key}
                    className={`cam-preset ${preset === p.key ? 'is-on' : ''}`} onClick={() => pickPreset(p.key)} data-testid={`va-preset-${p.key}`}>
                    <b>{p.label}</b><small>{p.hint}</small>
                  </button>
                ))}
              </div>
            </div>
            <div className="cam-field">
              <span className="cam-label">Resolución</span>
              <div className="cam-grid4" role="group" aria-label="Resolución">
                {HEIGHTS.map(([h, label]) => (
                  <button key={label} type="button" aria-pressed={height === h} className={`cam-chip ${height === h ? 'is-on' : ''}`}
                    onClick={() => custom(setHeight)(h)} data-testid={`va-h-${h || 'orig'}`}>{label}</button>
                ))}
              </div>
            </div>
            <div className="cam-grid2">
              {FPS.map(([f, label]) => (
                <button key={f} type="button" aria-pressed={fps === f} className={`cam-chip ${fps === f ? 'is-on' : ''}`}
                  onClick={() => custom(setFps)(f)}>{label}</button>
              ))}
            </div>
            <label className="cam-field">
              <span className="cam-label">Peso máximo (MB)</span>
              <input type="number" className="cam-input" min={1} max={500} step={1} value={maxMB ?? ''} placeholder="Sin tope"
                onChange={(e) => custom(setMaxMB)(e.target.value ? Number(e.target.value) : null)} data-testid="va-maxmb" />
            </label>
            <label className="cam-toggle">
              <span>Conservar audio</span>
              <input type="checkbox" checked={audio} onChange={(e) => custom(setAudio)(e.target.checked)} data-testid="va-audio" />
            </label>
            <div className="cam-tip" data-testid="va-result">
              <b>Resultado</b>
              <span>{meta && dims ? <>{fmtSec(len)} · {dims.width}×{dims.height} · {fps} fps · MP4 · {maxMB ? `hasta ${maxMB} MB` : `≈ ${fmtBytes(est)}`}</> : '—'}</span>
            </div>
            {problem && <p className="cam-problem" role="alert" data-testid="va-problem">{problem}</p>}
            <div className="cam-actions">
              <button type="button" className="cam-btn primary" onClick={save} disabled={!meta || !!problem || saving} data-testid="va-save">
                {saving ? <Loader2 size={16} className="cam-spin" aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                {saving ? 'Adaptando…' : 'Crear video adaptado'}
              </button>
              <button type="button" className="cam-btn ghost" onClick={onClose} disabled={saving}>Cancelar</button>
            </div>
          </aside>
        </div>
      </section>
    </div>,
    document.body,
  );
}
